import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

// Get escalation description based on level name and priority
const getEscalationDescription = (levelName: string, priority: number): string => {
  const descriptions: Record<string, string> = {
    'Level 1': 'First level escalation for new issues',
    'Level 2': 'Second level escalation for unresolved issues within 4 hours',
    'Level 3': 'Third level escalation for unresolved issues within 8 hours'
  };
  
  return descriptions[levelName] || `Escalation level ${priority} - handles issues at priority ${priority}`;
};

// Get escalation details (who to escalate to and method)
const getEscalationDetails = (levelName: string, priority: number): string => {
  const details: Record<string, string> = {
    'Level 1': 'Escalation to: Project Manager | Method: Email, Messaging | Timing: Immediately',
    'Level 2': 'Escalation to: Head of Services | Method: Email, Messaging, SMS | Timing: After 4 hours',
    'Level 3': 'Escalation to: Executive Management | Method: Email, SMS, Phone Call | Timing: After 8 hours'
  };
  
  return details[levelName] || `Escalation to: Level ${priority} Manager | Method: Email, Phone | Timing: As needed`;
};

interface EscalationLevel {
  id: string;
  project_id: string;
  name: string;
  description: string;
  priority: number;
  response_time: number;
  escalation_threshold: number;
  notification_methods: string[];
}

interface ChangeEscalationLevelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onChangeEscalationLevel: (levelId: string) => Promise<void>;
  currentLevelId: string;
  levels: EscalationLevel[];
}

export const ChangeEscalationLevelModal: React.FC<ChangeEscalationLevelModalProps> = ({
  isOpen,
  onClose,
  onChangeEscalationLevel,
  currentLevelId,
  levels
}) => {
  const [selectedLevelId, setSelectedLevelId] = useState(currentLevelId);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  const handleSubmit = async () => {
    if (selectedLevelId === currentLevelId) {
      onClose();
      return;
    }

    setIsLoading(true);
    try {
      await onChangeEscalationLevel(selectedLevelId);
      toast.success('Escalation level updated successfully');
      onClose();
    } catch (error) {
      toast.error('Failed to update escalation level');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-white bg-opacity-80 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="fixed top-4 right-4 z-[60] text-[#aaa] hover:text-[#555] p-2"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <div 
        className="bg-white rounded-lg w-full max-w-lg mx-4 max-h-[80vh] overflow-y-auto border border-[#eaeaea]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-[#eaeaea]">
          <h2 className="text-base font-semibold text-[#171717]">Change Escalation Level</h2>
        </div>

        {/* Content */}
        <div className="p-4">
          <div className="space-y-3">
            {/* General Category */}
            <div>
              <h4 className="text-xs font-medium text-[#444] mb-2 flex items-center">
                <span className="text-sm mr-1">⚡</span>
                General
              </h4>
              <div className="space-y-2 ml-4">
                <label
                  className={`block p-2 border rounded cursor-pointer transition-all ${
                    selectedLevelId === ''
                      ? 'border-[#888] bg-[#f5f5f5]'
                      : 'border-[#eaeaea] hover:border-[#eaeaea]'
                  }`}
                >
                  <div className="flex items-start">
                    <input
                      type="radio"
                      name="escalationLevel"
                      value=""
                      checked={selectedLevelId === ''}
                      onChange={(e) => setSelectedLevelId(e.target.value)}
                      className="mt-0.5 h-3 w-3 text-[#171717] focus:ring-0 border-[#eaeaea]"
                    />
                    <div className="ml-2 flex-1">
                      <div className="text-xs font-medium text-[#171717]">
                        Any
                      </div>
                      <div className="text-xs text-[#555] mt-0.5">
                        No specific escalation level assigned. Can handle issues at any level.
                      </div>
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* Escalation Levels Category */}
            {levels.filter(level => level.name.toLowerCase() !== 'any').length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-[#444] mb-2 flex items-center">
                  <span className="text-sm mr-1">🔄</span>
                  Escalation Levels
                </h4>
                <div className="space-y-2 ml-4">
                  {levels
                    .filter(level => level.name.toLowerCase() !== 'any')
                    .sort((a, b) => a.priority - b.priority)
                    .map((level) => (
                      <label
                        key={level.id}
                        className={`block p-2 border rounded cursor-pointer transition-all ${
                          selectedLevelId === level.id
                            ? 'border-[#888] bg-[#f5f5f5]'
                            : 'border-[#eaeaea] hover:border-[#eaeaea]'
                        }`}
                      >
                        <div className="flex items-start">
                          <input
                            type="radio"
                            name="escalationLevel"
                            value={level.id}
                            checked={selectedLevelId === level.id}
                            onChange={(e) => setSelectedLevelId(e.target.value)}
                            className="mt-0.5 h-3 w-3 text-[#171717] focus:ring-0 border-[#eaeaea]"
                          />
                          <div className="ml-2 flex-1">
                            <div className="text-xs font-medium text-[#171717]">
                              {level.name}
                            </div>
                            <div className="text-xs text-[#555] mt-0.5">
                              {level.description || getEscalationDescription(level.name, level.priority)}
                            </div>
                            <div className="text-xs text-[#888] mt-0.5">
                              Priority: {level.priority} | Response Time: {level.response_time}h
                            </div>
                            <div className="text-xs text-[#888] mt-0.5">
                              {getEscalationDetails(level.name, level.priority)}
                            </div>
                          </div>
                        </div>
                      </label>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-[#eaeaea]">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-xs text-[#555] border border-[#eaeaea] rounded hover:bg-[#fafafa] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="px-3 py-1.5 text-xs bg-[#171717] text-white rounded hover:bg-[#333] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? 'Changing...' : 'Change Level'}
          </button>
        </div>
      </div>
    </div>
  );
}; 